import { Box, Divider, Icon } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useCanEditUnassignedShift } from 'modules/shift_management/hooks/useCanEditUnassignedShift'
import { useDestroyUnassignedShift } from 'modules/shift_management/hooks/useDestroyUnassignedShift'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import type UnassignedShift from 'modules/shift_management/store/internal/models/UnassignedShift'

interface CellProps {
  unassignedShift: UnassignedShift
  disabled: boolean
  onAssignEmployeesAction: () => void
  onSeeShiftAction: () => void
  onEditAction: () => void
  contractVersions: ContractVersions
}

const UnassignedShiftMenu = ({
  unassignedShift,
  disabled,
  onAssignEmployeesAction,
  onSeeShiftAction,
  onEditAction,
  contractVersions,
}: CellProps) => {
  const { onDestroy } = useDestroyUnassignedShift(unassignedShift, contractVersions)
  const { closePortal } = usePortal()

  const onDeleteAction = () => {
    onDestroy(closePortal)
  }

  const unassignedShiftsModel = unassignedShift.toPayload()
  const canEditUnassignedShifts = useCanEditUnassignedShift(unassignedShiftsModel)

  const [{ shiftsRows }] = useShiftsRows()
  const canManageShifts = shiftsRows.some((shiftsRow) => shiftsRow.canManage)

  return (
    <Box height="s16" width="s16" onClick={(e: MouseEvent) => e.stopPropagation()}>
      <PopoverMenu type="list">
        <PopoverMenu.Trigger>
          <Box justifyContent="center" paddingY="s2">
            <Icon.Small icon="ellipsis" color="grey1000" />
          </Box>
        </PopoverMenu.Trigger>

        <PopoverMenu.Content side="bottom" align="end" width="s240">
          {canManageShifts && (
            <>
              <PopoverMenu.TouchableOption
                icon="user"
                onClick={onAssignEmployeesAction}
                title={i18n.t('shift_management.shift_menu_options.assign_employees')}
                disabled={disabled}
              />
              <Divider />
            </>
          )}

          <PopoverMenu.TouchableOption
            icon="eye-visible"
            onClick={onSeeShiftAction}
            title={i18n.t('shift_management.shift_menu_options.show_details')}
          />

          {canEditUnassignedShifts && (
            <>
              <PopoverMenu.TouchableOption
                icon="pencil"
                onClick={onEditAction}
                title={i18n.t('shift_management.shift_menu_options.edit')}
              />
              <Divider />
              <PopoverMenu.TouchableOption
                icon="delete"
                onClick={onDeleteAction}
                title={i18n.t('shift_management.shift_menu_options.delete')}
                danger
              />
            </>
          )}
        </PopoverMenu.Content>
      </PopoverMenu>
    </Box>
  )
}

export default observer(UnassignedShiftMenu)
